import { HttpParams } from "@angular/common/http";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { GoogleMapsApiService, Option } from "@recore-solutions/nstar-lib";
import { Observable, of, Subscription } from "rxjs";
import { filter, map, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { County, CountyAndState, StateOrProvince } from "../../interfaces/county-and-state";
import { PreferencesService } from "../../../preferences/preferences.service";
import { ListingService } from "../../services/listing.service";

@Component({
	selector: "app-county-select",
	templateUrl: "./county-select.component.html",
	styleUrls: ["./county-select.component.scss"],
})
export class CountySelectComponent implements OnInit, OnDestroy {
	@Input() control: UntypedFormControl;
	@Input() placeholder: string;
	@Output() addressSelected: EventEmitter<google.maps.places.PlaceResult> = new EventEmitter();
	@Output() countySelected: EventEmitter<CountyAndState> = new EventEmitter();
	countyAndState$: Observable<(Option & CountyAndState)[]>;

	private subscriptions: Subscription[] = [];

	onChangeValueMapper = (option: Option & google.maps.places.AutocompletePrediction): Option => {
		return option;
	};
	optionsMatchingTermFn = (term: string) => (options: Option[]) =>
		options.filter((o) => !o.hidden && this.cleanName(o.value).indexOf(this.cleanName(term)) > -1);
	selectItemMapper = (event: NgbTypeaheadSelectItemEvent) => {
		if (!event?.item) {
			return event;
		} else if (!!event.item.countyName) {
			return event.item;
		} else if (!!event.item.place_id) {
			const place$ = this.googleMapsService.getDetail$({ placeId: event.item.place_id });
			const countyAndState$ = place$.pipe(switchMap(this.switchDetailsToStateCounty$(event.item)));
			return countyAndState$;
		}
	};

	constructor(
		private listingService: ListingService,
		private preferencesService: PreferencesService,
		private googleMapsService: GoogleMapsApiService
	) {
		this.countyAndState$ = this.queryCountyAndStateOption$(new HttpParams({ fromObject: {} }));
	}

	ngOnInit(): void {
		this.placeholder = this.placeholder || "Search for a county";
		this.setDefaultCounty(this.control);
	}

	queryCountyAndStateOption$ = (
		httpParams: HttpParams
	): Observable<(Option & StateOrProvince & County)[]> =>
		this.listingService
			.getGenericProcValues<CountyAndState>("Polaris.spGetCounty", httpParams)
			.pipe(map(this.mapCountiesAndStates), shareReplay({ refCount: true, bufferSize: 1 }));

	setDefaultCounty(formControl: UntypedFormControl): void {
		if (this.placeholder === "Search for a county") {
			this.preferencesService
				.getPreference(["autopop", "county"])
				.pipe(
					take(1),
					filter((county) => !!county),
					tap((county) => formControl.setValue(county))
				)
				.subscribe();
		} else {
			formControl.setValue(null);
		}
	}

	searchFn = (text: string): Observable<any[]> => {
		if (!text || text.length <= 2) {
			return of([]);
		} else if (/\d/.test(text)) {
			return this.findAddress(text);
		} else {
			return this.findStateCounty(text);
		}
	};

	resultTransformFn = (result: any[]): Option[] => {
		return (result || []).map((r) => ({
			...r,
			key: r.place_id || r.key,
			value: r.description || r.value || "",
			sortOrder: 0,
		}));
	};

	findAddress = (text: string): Observable<any[]> => {
		return this.googleMapsService.apiLoaded$.pipe(
			switchMap(() => this.googleMapsService.getPlacePrediction$({ input: text })),
			map(([predictions]) => predictions)
		);
	};

	findStateCounty = (text: string): Observable<any[]> => {
		const httpParams = new HttpParams({
			fromObject: {
				countyName: `~${text}`,
			},
		});
		return this.listingService
			.getGenericProcValues<CountyAndState>("Polaris.spGetCounty", httpParams)
			.pipe(map(this.mapCountiesAndStates), shareReplay({ refCount: true, bufferSize: 1 }));
	};

	mapCountiesAndStates(countiesAndStates: CountyAndState[]): (CountyAndState & Option)[] {
		return countiesAndStates.map((c) => ({
			...c,
			key: c.countyFIPSCD.replace(c.stateOrProvinceSK.toString(), c.stateOrProvinceCD),
			value: [c.countyName, c.stateOrProvinceCD].join(", "),
			rprAddress: c.countyName + " County, " + c.stateOrProvinceCD,
			sortOrder: 0,
		}));
	}

	switchDetailsToStateCounty$ =
		(suggestion: google.maps.places.AutocompletePrediction) =>
		(details: google.maps.places.PlaceResult) => {
			const stateOrProvinceCD = this.googleMapsService.googlePlaceState(details);
			const countyName = this.googleMapsService.googlePlaceCounty(details);
			const httpParams = new HttpParams({
				fromObject: {
					StateOrProvince_CD: stateOrProvinceCD,
					countyName: `~${countyName}`,
				},
			});
			return this.queryCountyAndStateOption$(httpParams).pipe(
				map((stateCounties) =>
					stateCounties.find(
						(sc) =>
							sc.stateOrProvinceCD === stateOrProvinceCD &&
							this.cleanName(sc.countyName) === this.cleanName(countyName)
					)
				),
				map((stateCounty) =>
					!!stateCounty
						? {
								...details,
								...stateCounty,
								value: suggestion.description,
						  }
						: stateCounty
				)
			);
		};

	cleanName(name: string): string {
		return !!name
			? name
					.toLocaleLowerCase()
					.replace(/\./g, "")
					.replace(/(^st$)|(^st )|( st$)|( st )/, " saint ")
					.trim()
			: "";
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}
}
